import "./SealGuidelinesPage.scss";
import { AppSeo } from "../../../components/app-seo/AppSeo";
import { useNavigate } from "react-router-dom";
import { Paths } from "../../../paths";
import sealClearspaceLong from "../../../images/seal/seal-clearspace-long.jpg";
import sealClearspaceShort from "../../../images/seal/seal-clearspace-short.jpg";

export const SealGuidelinesPage = () => {
  const navigate = useNavigate();

  return (
    <>
      <AppSeo
        title="OVR Seal Guidelines"
        description="Official usage guidelines for the OVR Seal, including artwork specifications, placement rules, and permitted uses on wine packaging and marketing materials."
        keywords="OVR seal, usage guidelines, old vine seal, wine labeling, old vine registry, seal artwork, packaging guidelines"
      />
      <div className="about-layout">
        <div>
          <p className="description-text">
            <a
              className="description-text-link"
              href={Paths.seal}
              onClick={(e) => {
                e.preventDefault();
                navigate(Paths.seal);
              }}
            >
              &larr; Back to the OVR Seal
            </a>
          </p>

          <h1 className="title-large">OVR Seal Usage Guidelines</h1>
          <p className="sub-headline u-mt-small">
            Effective date: 1 October, 2026 &middot; Version: 1.0
          </p>

          {/* Section 1 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">1. Before You Use the OVR Seal</h2>
            <p className="description-text">
              Before using the OVR Seal, confirm that:
            </p>
            <ul className="description-text">
              <li>
                The wine brand, vineyard business, importer, or retailer using
                the seal is registered directly with the program.
              </li>
              <li>
                OVR has confirmed the registration and supplied the official
                artwork.
              </li>
              <li>
                The use is limited to the name identified in the registration.
              </li>
              <li>
                At least 85% of the wine's grapes come from OVR-registered
                vineyards.
              </li>
              <li>
                At least 85% meet or exceed the age shown on the selected seal.
              </li>
              <li>
                The consumer-facing wine brand is registered if the seal appears
                on packaging.
              </li>
              <li>
                The artwork has not been transferred to another brand or
                organization.
              </li>
              <li>The use complies with the current Usage Guidelines.</li>
            </ul>
            <p className="description-text">
              Registration and vineyard review may take 1–3 weeks. Submission
              and payment do not authorize immediate use.
            </p>
          </div>

          {/* Section 2 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">2. Purpose of the OVR Seal</h2>
            <p className="description-text">
              The Old Vine Registry Seal helps consumers identify wines made
              with grapes from old-vine vineyards registered in the Old Vine
              Registry.
            </p>
            <p className="description-text">
              The seal connects eligible wines to the vineyard information
              available in the OVR and provides a consistent way for authorized
              organizations to communicate that connection on packaging,
              websites, sales materials, and other approved applications.
            </p>
            <p className="description-text">
              The OVR Seal indicates vineyard source and age eligibility as
              defined in these guidelines. It is not a certification of wine
              quality, sustainability, farming practices, winemaking methods, or
              other product attributes.
            </p>
          </div>

          {/* Section 3 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">3. Who May Use the Seal</h2>
            <p className="description-text">
              The OVR Seal may be used only by a participant that has registered
              directly with the OVR Seal program and received confirmation from
              the Old Vine Registry.
            </p>
            <p className="description-text">
              Eligible participants include:
            </p>
            <ul className="description-text">
              <li>Vineyard owners and operators</li>
              <li>Wine producers</li>
              <li>Wine brand owners</li>
              <li>Importers</li>
              <li>Retailers</li>
            </ul>
            <p className="description-text">
              Distributors, wholesalers, regional marketing bodies, trade
              associations, agencies, consultants, and other third parties are
              not eligible unless they also qualify under one of the roles
              above.
            </p>
            <p><div className="seal-landing-info-box u-mt-medium">
              <p className="description-text">
                <strong>South African Exception</strong>
              </p>
              <p className="description-text">
                The OVR Seal is not issued to or valid for use by South African vineyards or wine producers under any circumstances. This is because South Africa already has in place their <a href="https://www.oldvineproject.co.za/aboutus/certifiedheritagevineyardseal">Certified Heritage Vineyard</a> program developed by the <a href="https://www.oldvineproject.co.za">Old Vine Project</a>.  Interested producers from South Africa should engage with the <a href="https://www.oldvineproject.co.za">Old Vine Project</a> and utilize the CHV initiative in lieu of the OVR Seal.
              </p>
            </div></p>
            <p className="description-text">
              <strong>Registration scope</strong>
            </p>
            <p className="description-text">
              Each registration covers one named participant:
            </p>
            <ul className="description-text">
              <li>One consumer-facing wine brand</li>
              <li>One vineyard business</li>
              <li>One importing business</li>
              <li>One retail business</li>
            </ul>
            <p className="description-text">
              <strong>
                One registration does not cover an entire portfolio of
                separately marketed wine brands.
              </strong>
            </p>
            <p className="description-text">
              Additional wine brands require separate registrations, even when
              they share the same parent company, legal owner, producer,
              importer, or management.
            </p>
            <p className="description-text">
              A registered wine brand may use the appropriate seal for all
              eligible wines and vintages sold under that brand. Individual
              wines and vintages do not require separate registrations.
            </p>
            <p className="description-text">
              <strong>Artwork transfer</strong>
            </p>
            <p className="description-text">
              Seal artwork may not be given, sold, sublicensed, distributed, or
              otherwise provided to another wine brand or organization for its
              independent use. A registered participant may provide artwork to a
              printer, designer, web developer, or other service provider
              working solely on its behalf. The service provider receives no
              independent right to retain, reuse, or provide the artwork to
              another party.
            </p>
            <p className="description-text">
              <strong>Use on wine packaging</strong>
            </p>
            <p className="description-text">
              To use the OVR Seal on a bottle, label, closure, capsule, carton,
              case, or other wine packaging, the consumer-facing wine brand
              shown on that packaging must be registered.
            </p>
            <p className="description-text">
              The registration must be submitted by the wine producer, wine
              brand owner, or another eligible organization authorized to
              control packaging for that brand.
            </p>
            <p className="description-text">
              Registration of a parent company, vineyard business, importer,
              retailer, or another wine brand does not authorize packaging use
              by an unregistered brand. Each separately marketed wine brand
              using the seal on packaging requires its own registration.
            </p>
            <p className="description-text">
              <strong>Importer and retailer use</strong>
            </p>
            <p className="description-text">
              Registered importers and retailers may display the appropriate
              seal beside eligible wines on their own websites, e-commerce
              listings, catalogs, wine lists, shelf talkers, retail displays,
              and promotional materials.
            </p>
            <p className="description-text">
              Their registration applies only to communications issued by the
              registered importer or retailer. It does not register the wine
              brands they carry or authorize those brands or producers to use
              the seal on packaging.
            </p>
            <p className="description-text">
              Importers and retailers may not provide the artwork to another
              organization for its independent use.
            </p>
            <p className="description-text">
              <strong>Vineyard-business use</strong>
            </p>
            <p className="description-text">
              A registered vineyard owner or operator may use the appropriate
              seal in connection with its registered vineyard, including on its
              website, promotional materials, and vineyard signage.
            </p>
            <p className="description-text">
              Vineyard-business registration does not authorize an unregistered
              wine brand to use the seal on wine packaging. Signage must not
              imply that every block on a property meets the displayed age or
              that every wine associated with the vineyard qualifies.
            </p>
            <p className="description-text">
              <strong>Registration review and authorization</strong>
            </p>
            <p className="description-text">
              Registration and vineyard review may take 1–3 weeks. Payment does
              not authorize immediate use of the OVR Seal.
            </p>
            <p className="description-text">
              A participant may begin using the seal only after OVR confirms
              the registration and provides the official artwork and current
              Usage Guidelines. OVR may contact the applicant if additional
              vineyard or registration information is needed.
            </p>
          </div>

          {/* Section 4 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">4. Which Wines Qualify</h2>
            <p className="description-text">
              A wine qualifies for an OVR Seal when at least 85% of its grapes
              come from one or more vineyards registered in the Old Vine
              Registry.
            </p>
            <p className="description-text">
              The registered organization is responsible for confirming that
              each wine on which it uses the seal meets this requirement.
            </p>
            <p className="description-text">
              The remaining portion of the wine does not need to come from an
              OVR-registered vineyard, but it may not be used to support the
              age claim shown on the seal.
            </p>
            <p className="description-text">
              Eligibility applies to wine made from grapes grown in qualifying
              vineyards. Registration of a vineyard property does not
              automatically make all wine associated with that property
              eligible if the grapes used in the wine do not come from the
              registered vines or vineyard area represented by the OVR record.
            </p>
          </div>

          {/* Section 5 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">
              5. Choosing the Correct Age Seal
            </h2>
            <p className="description-text">
              OVR Seals are available for the following age thresholds:
            </p>
            <ul className="description-text">
              <li>35+ years</li>
              <li>50+ years</li>
              <li>75+ years</li>
              <li>100+ years</li>
              <li>150+ years</li>
            </ul>
            <p className="description-text">
              To use a particular age seal, at least 85% of the wine's grapes
              must come from OVR-registered vineyards whose displayed ages meet
              or exceed the threshold shown on the seal.
            </p>
            <p className="description-text">
              A wine should use the highest age seal for which at least 85% of
              its grapes qualify.
            </p>
            <p className="description-text">
              <strong>Example</strong>
            </p>
            <p className="description-text">A wine containing:</p>
            <ul className="description-text">
              <li>60% fruit from a registered 100-year-old vineyard</li>
              <li>25% fruit from a registered 75-year-old vineyard</li>
              <li>15% fruit from another source</li>
            </ul>
            <p className="description-text">
              may use the 75+ seal because 85% of the wine comes from
              registered vineyards at least 75 years old.
            </p>
            <p className="description-text">
              It may not use the 100+ seal because only 60% of the wine comes
              from vineyards meeting that threshold.
            </p>
            <p className="description-text">
              <strong>Vineyard planting dates</strong>
            </p>
            <p className="description-text">
              Each vineyard's age is calculated from the four-digit planting
              year displayed in its OVR record.
            </p>
            <p className="description-text">
              The planting year may be exact or estimated. When a date is
              estimated, the vineyard record generally (but not always) explains
              the basis for that estimate. Both exact and estimated dates may be
              used to determine the appropriate age seal.
            </p>
            <p className="description-text">
              The applicable vineyard age should be calculated for the vintage
              year in which the grapes were harvested, not the year in which the
              wine is released or sold.
            </p>
            <p className="description-text">
              For a non-vintage wine, the registered organization must use an
              age seal supported by at least 85% of the grapes contributing to
              the finished wine.
            </p>
          </div>

          {/* Section 6 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">
              6. Responsibility of the Registered Participant
            </h2>
            <p className="description-text">
              The Old Vine Registry licenses the seal for use by individual
              registered participants rather than licensing individual wines.
            </p>
            <p className="description-text">
              Each registered participant is responsible for:
            </p>
            <ul className="description-text">
              <li>Confirming wine eligibility</li>
              <li>Selecting the correct age seal</li>
              <li>
                Using the seal only for the name covered by its registration
              </li>
              <li>Following the rules permitted for its role</li>
              <li>Using only official artwork</li>
              <li>Keeping reasonable supporting information</li>
              <li>Correcting inaccurate or misleading uses</li>
              <li>Responding to OVR questions</li>
              <li>
                Not transferring artwork to another brand or organization
              </li>
            </ul>
            <p className="description-text">
              OVR's confirmation of a registration does not constitute separate
              approval of every wine, vintage, label, website listing, or
              marketing application used by the registered participant.
            </p>
          </div>

          {/* Section 7 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">7. Permitted Uses</h2>
            <p className="description-text">
              Subject to these guidelines, a registered organization may use
              the appropriate OVR Seal in connection with eligible wines.
            </p>
            <p className="description-text">
              Permitted uses may include:
            </p>
            <ul className="description-text">
              <li>
                Wine labels and packaging, when used by a registered producer
              </li>
              <li>Winery or brand websites</li>
              <li>Importer or retailer websites</li>
              <li>E-commerce product listings</li>
              <li>Digital and printed sales materials</li>
              <li>Shelf talkers and retail displays</li>
              <li>Restaurant wine lists</li>
              <li>Trade presentations</li>
              <li>Advertisements</li>
              <li>Social media posts</li>
              <li>Vineyard or winery signage</li>
              <li>
                Cases and shipping materials associated with eligible wines
              </li>
              <li>Event materials promoting eligible wines</li>
            </ul>
            <p className="description-text">
              The seal must appear in a context that clearly connects it to the
              qualifying wine or vineyard. It must not be placed in a way that
              implies that every wine made, imported, distributed, or sold by
              an organization qualifies.
            </p>
            <p className="description-text">
              <strong>Participant-level use</strong>
            </p>
            <p className="description-text">
              A registered participant may state that it is an "Authorized
              Participant in the OVR Seal Program." This statement applies only
              to the wine brand, vineyard business, importer, or retailer named
              in the registration.
            </p>
            <p className="description-text">
              The seal may not be used as a general corporate or portfolio
              endorsement or in a way that implies OVR has approved every wine
              or brand associated with a parent company.
            </p>
            <p className="description-text">
              When several wines appear together, the seal must be positioned
              so consumers can determine which wines qualify.
            </p>
          </div>

          {/* Section 8 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">8. Packaging Use</h2>
            <p className="description-text">
              A registered producer may place the appropriate seal on packaging
              for an eligible wine.
            </p>
            <p className="description-text">
              Acceptable placements may include:
            </p>
            <ul className="description-text">
              <li>Front label</li>
              <li>Back label</li>
              <li>Neck label</li>
              <li>Capsule or closure</li>
              <li>Bottle tag</li>
              <li>Sticker or decal</li>
              <li>Carton or gift box</li>
              <li>Case packaging</li>
            </ul>
            <p className="description-text">
              The seal should be placed where it is legible and visually
              associated with the qualifying wine.
            </p>
            <p className="description-text">
              Before printing packaging, the producer should confirm:
            </p>
            <ul className="description-text">
              <li>The wine meets the 85% rule</li>
              <li>The correct age seal has been selected</li>
              <li>The seal artwork has not been altered</li>
              <li>
                The seal meets the minimum-size and clear-space requirements
                (see below)
              </li>
              <li>
                The surrounding wording does not overstate or misrepresent the
                meaning of the seal
              </li>
              <li>
                The intended use complies with applicable labeling laws in the
                markets where the wine will be sold
              </li>
            </ul>
            <p className="description-text">
              OVR registration does not replace government label approval or
              relieve a producer of responsibility for complying with applicable
              alcohol-labeling, advertising, trademark, or consumer-protection
              requirements as well as any regional appellation or wine
              regulatory rules associated with labeling or use of specific
              terms.
            </p>
          </div>

          {/* Section 9 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">9. Websites and E-Commerce</h2>
            <p className="description-text">
              A registered organization may display the appropriate seal on a
              webpage or e-commerce listing for an eligible wine.
            </p>
            <p className="description-text">The seal should:</p>
            <ul className="description-text">
              <li>
                Appear close to the relevant wine name, image, or description
                OR next to the description of the vineyard itself if such a
                description exists
              </li>
              <li>Be displayed only on listings for qualifying wines</li>
              <li>Remain legible at common desktop and mobile sizes</li>
              <li>
                Link to the relevant OVR vineyard record or records whenever
                practical
              </li>
              <li>Include accessible alternative text</li>
            </ul>
            <p className="description-text">
              <strong>Recommended alternative text:</strong>
            </p>
            <p className="description-text">
              "Old Vine Registry [age]+ Seal"
            </p>
            <p className="description-text">
              The seal must not appear in a website header, footer, navigation
              bar, or other global location as doing so could imply that every
              wine on the site qualifies.
            </p>
            <p className="description-text">
              A registered importer or retailer may use the seal on its own
              website for an eligible wine but may not provide the artwork to
              an unregistered wine brand for packaging use.
            </p>
          </div>

          {/* Section 10 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">
              10. Wine Lists, Shelf Talkers, and Multi-Product Displays
            </h2>
            <p className="description-text">
              When the seal appears on a wine list, shelf talker, catalog,
              tasting sheet, or display containing several wines, it must be
              clear which specific wine or wines qualify.
            </p>
            <p className="description-text">
              Recommended approaches include:
            </p>
            <ul className="description-text">
              <li>Placing the seal directly beside each qualifying wine</li>
              <li>
                Using a symbol beside qualifying wines with a nearby
                explanation
              </li>
              <li>
                Creating a clearly separated section for qualifying wines
              </li>
              <li>
                Including a short explanation of what the seal means
              </li>
            </ul>
            <p className="description-text">
              Do not place a single seal above a group of wines if some of
              those wines do not qualify.
            </p>
          </div>

          {/* Section 11 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">
              11. Vineyard and Winery Signage
            </h2>
            <p className="description-text">
              A registered vineyard owner or producer may use the seal on
              signage associated with a registered vineyard.
            </p>
            <p className="description-text">
              Signage must use the age seal supported by the planting date
              displayed in the vineyard's OVR record. Versions of the OVR Seal
              without numeric ages are available for signage purposes.
            </p>
            <p className="description-text">
              The seal must not imply that:
            </p>
            <ul className="description-text">
              <li>Every block on a property is the age shown</li>
              <li>Every wine made by the winery qualifies</li>
              <li>
                OVR has certified the vineyard's farming practices, condition,
                or wine quality
              </li>
            </ul>
            <p className="description-text">
              When practical, signage should include a QR code or URL linking
              visitors to the vineyard's OVR record.
            </p>
          </div>

          {/* Section 12 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">12. Approved Artwork</h2>
            <p className="description-text">
              Registered organizations must use only official seal files
              supplied by the Old Vine Registry.
            </p>
            <p className="description-text">Do not:</p>
            <ul className="description-text">
              <li>Redraw or recreate the seal</li>
              <li>Change its wording or age</li>
              <li>Alter its proportions</li>
              <li>Stretch, compress, rotate, or distort it</li>
              <li>
                Change its colors except by using an approved color version
              </li>
              <li>Rearrange, remove, or add design elements</li>
              <li>
                Apply effects such as shadows, outlines, gradients, or bevels
              </li>
              <li>Place other text or imagery inside the seal</li>
              <li>
                Combine the seal with another logo to create a new mark
              </li>
              <li>
                Use a low-resolution screenshot or copy taken from another
                website or package
              </li>
              <li>Use an age seal for which the wine does not qualify</li>
            </ul>
            <p className="description-text">
              Use the file format appropriate to the application:
            </p>
            <ul className="description-text">
              <li>
                Print-quality vector artwork for labels, packaging, and printed
                materials
              </li>
              <li>
                Approved digital artwork for websites, e-commerce,
                presentations, and social media
              </li>
            </ul>
          </div>

          {/* Section 13 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">
              13. Size, Clear Space, Color, and Background
            </h2>
            <p className="description-text">
              <strong>Clear space</strong>
            </p>
            <p className="description-text">
              Maintain clear space equal to one quarter of the seal's visible
              artwork height on all four sides (x = H / 4).
            </p>
            <p className="description-text">
              H is the visible height of the complete seal artwork. Measure
              from the furthest visible point, including the tree, wording, age
              designation, and plus sign — not from the file canvas or
              artboard.
            </p>
            <p className="description-text">
              The background may continue through the clear space. Type, marks,
              borders, trim, folds, and high-contrast visual details may not
              enter it.
            </p>
            <p className="description-text">
              <strong>Size requirements</strong>
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "24px", margin: "16px 0 24px" }}>
              <div>
                <p className="description-text" style={{ marginBottom: "8px" }}><strong>Long Format</strong></p>
                <img
                  src={sealClearspaceLong}
                  alt="Long format clearspace sample"
                  style={{ maxWidth: "500px", width: "100%", height: "auto" }}
                />
              </div>
              <div>
                <p className="description-text" style={{ marginBottom: "8px" }}><strong>Short Format</strong></p>
                <img
                  src={sealClearspaceShort}
                  alt="Short format clearspace sample"
                  style={{ maxWidth: "320px", width: "100%", height: "auto" }}
                />
              </div>
            </div>
            <p className="description-text">
              <em>Long format</em> — Recommended production width at least:
              48 mm / 1.89 in. Minimum width: 36 mm / 1.42 in.
            </p>
            <p className="description-text">
              <em>Short format</em> — Recommended production width at least:
              21 mm / 0.83 in. Minimum width: 16.5 mm / 0.65 in.
            </p>
            <p className="description-text">
              Measure the width of the visible artwork only. Required clear
              space is additional. Use the recommended width for routine
              production. Never reproduce the seal below its minimum width.
              Regardless of size chosen, Make sure that the URL is readable.
              Different printing techniques and media will result in different
              readability, and may require a different sizing. If a production
              method cannot retain the artwork's fine details at the minimum
              size, reproduce it at a larger size.
            </p>
            <p className="description-text" style={{ color: "#B3261E" }}>Different printing techniques and media will result in different readability, and may require a different sizing. If a production method cannot retain the artwork's fine details at the minimum size, reproduce it at a larger size.</p>
            <p className="description-text">
              <strong>Application rules</strong>
            </p>
            <ul className="description-text">
              <li>Scale the complete seal proportionally.</li>
              <li>
                Keep the required clear space from page and label edges, trim,
                folds, bottle seams, nearby marks, regulatory copy, and other
                type.
              </li>
              <li>
                Use a uniform, high-contrast field. Background color may
                continue through the clear space; competing detail may not.
              </li>
              <li>
                Never crop, stretch, rearrange, rotate, add effects, or alter
                the spacing among the tree, divider, wording, age designation,
                and plus sign.
              </li>
              <li>
                Treat the plus sign as part of the visible artwork boundary
                whenever it is present.
              </li>
              <li>
                Use approved master artwork. Proof specialty reproduction at
                actual size and enlarge the artwork when the production process
                loses fine detail.
              </li>
            </ul>
            <p className="description-text" style={{ fontStyle: "italic", color: "#616161", fontSize: "1.6rem" }}>
              Diagrams marked SAMPLE are for reference only and must not be
              used as production artwork.
            </p>
            <p className="description-text">
              <strong>Approved colors</strong>
            </p>
            <p className="description-text">
              Use only the official artwork versions provided by OVR:
            </p>
            <ul className="description-text">
              <li>Black version</li>
              <li>White/reversed version</li>
            </ul>
            <p className="description-text">
              Choose the version that provides sufficient contrast with the
              background.
            </p>
            <p className="description-text">
              If your design requires a color other than black or white, please contact the OVR for approval: <a href="mailto:oldvineregistry@gmail.com">
                oldvineregistry@gmail.com
              </a>
            </p>
            <p className="description-text">
              <strong>Backgrounds</strong>
            </p>
            <p className="description-text">
              Place the seal on a simple background that preserves legibility.
            </p>
            <p className="description-text">Do not place it over:</p>
            <ul className="description-text">
              <li>Visually busy imagery</li>
              <li>Patterns that obscure the wording</li>
              <li>Colors with insufficient contrast</li>
              <li>
                Bottle features, folds, seams, or edges that distort the seal
              </li>
            </ul>
          </div>

          {/* Section 14 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">14. Accompanying Language</h2>
            <p className="description-text">
              Organizations may use factual language that accurately explains
              the seal.
            </p>
            <p className="description-text">
              <strong>Recommended language</strong>
            </p>
            <p className="description-text">
              "This wine is made with grapes from vineyards registered in the
              Old Vine Registry."
            </p>
            <p className="description-text">
              "This wine qualifies for the Old Vine Registry [age]+ Seal."
            </p>
            <p className="description-text">
              "Made with at least 85% of its grapes from Old Vine Registry
              vineyards aged [age] years or more."
            </p>
            <p className="description-text">
              "Learn more about the vineyard in the Old Vine Registry."
            </p>
            <p className="description-text">
              <strong>Language that must not be used</strong>
            </p>
            <p className="description-text">
              Do not describe a wine, vineyard, or organization as:
            </p>
            <ul className="description-text">
              <li>Certified by the Old Vine Registry</li>
              <li>Approved by the Old Vine Registry</li>
              <li>Guaranteed by the Old Vine Registry</li>
              <li>Endorsed by the Old Vine Registry</li>
              <li>Quality-certified</li>
              <li>Sustainability-certified</li>
              <li>Authenticated by OVR</li>
            </ul>
            <p className="description-text">
              Do not state or imply that OVR independently verified the
              composition, quality, provenance, production methods, or
              regulatory compliance of a particular wine.
            </p>
            <p className="description-text">
              Avoid phrases such as "OVR-certified old-vine wine" or
              "officially certified old vines."
            </p>
          </div>

          {/* Section 15 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">
              15. Connecting Consumers to the Registry
            </h2>
            <p className="description-text">
              Whenever practical, digital uses of the seal should link directly
              to the relevant vineyard record or records in the Old Vine
              Registry.
            </p>
            <p className="description-text">
              Printed materials may include:
            </p>
            <ul className="description-text">
              <li>The vineyard's OVR page URL</li>
              <li>A QR code linking to the vineyard record</li>
              <li>
                A short invitation such as "Discover this vineyard in the Old
                Vine Registry"
              </li>
            </ul>
            <p className="description-text">
              A QR code or URL must not interfere with the seal's required
              clear space.
            </p>
          </div>

          {/* Section 16 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">
              16. Changes to a Wine or Vineyard Record
            </h2>
            <p className="description-text">
              A registered organization should reassess a wine's eligibility
              whenever:
            </p>
            <ul className="description-text">
              <li>Its vineyard sources change</li>
              <li>
                The percentages supplied by its vineyards change
              </li>
              <li>A new vintage is produced</li>
              <li>
                The wine becomes non-vintage or its blend composition changes
              </li>
              <li>
                A vineyard's OVR record is corrected or materially revised
              </li>
              <li>A vineyard is removed from the OVR</li>
              <li>
                New information calls the vineyard's recorded planting date
                into question
              </li>
            </ul>
            <p className="description-text">
              Artwork already applied to finished packaging should be addressed
              in consultation with OVR if a later record change affects the
              wine's eligibility.
            </p>
            <p className="description-text">
              A licensee should not continue applying the seal to new packaging
              or marketing materials once it knows that the wine no longer
              qualifies.
            </p>
          </div>

          {/* Section 17 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">17. Misuse and Corrections</h2>
            <p className="description-text">
              The Old Vine Registry may review a use of the seal after
              receiving a question, complaint, or community report.
            </p>
            <p className="description-text">OVR may:</p>
            <ul className="description-text">
              <li>Request clarification or supporting information</li>
              <li>
                Require correction of an inaccurate badge or statement
              </li>
              <li>Require removal from controllable digital materials</li>
              <li>Prohibit new printing or production</li>
              <li>
                Establish a reasonable correction plan for existing materials
              </li>
              <li>Withdraw recognition of the named participant</li>
              <li>
                Remove the participant from any public authorized-participant
                listing
              </li>
            </ul>
            <p className="description-text">
              OVR recognizes that packaging already in circulation may not
              always be immediately recoverable. When appropriate, OVR and the
              registered participant may agree on a reasonable correction plan
              for future production and communications.
            </p>
            <p className="description-text">
              OVR may withdraw its recognition of a participant when seal use
              is materially inaccurate, misleading, unauthorized, or
              inconsistent with these guidelines. Suspension or termination
              applies to the named participant and does not transfer
              authorization to a parent company, affiliated brand, or other
              organization.
            </p>
            <p className="description-text">
              Removal from OVR's list of authorized participants does not
              transfer or preserve any right to continue using the seal.
            </p>
          </div>

          {/* Section 18 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">
              18. Reporting Possible Misuse
            </h2>
            <p className="description-text">
              Questions or suspected misuse may be reported to:
            </p>
            <p className="description-text">
              <a href="mailto:oldvineregistry@gmail.com">
                oldvineregistry@gmail.com
              </a>
            </p>
            <p className="description-text">
              Please include, when available:
            </p>
            <ul className="description-text">
              <li>The organization or wine involved</li>
              <li>
                A photograph, screenshot, or link showing the use
              </li>
              <li>The location or market where it appeared</li>
              <li>A short explanation of the concern</li>
            </ul>
            <p className="description-text">
              OVR will review reports in context and may contact the
              organization involved for clarification.
            </p>
          </div>

          {/* Section 19 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">
              19. Updates to These Guidelines
            </h2>
            <p className="description-text">
              The Old Vine Registry may update these guidelines as the program
              develops.
            </p>
            <p className="description-text">
              Registered organizations are responsible for following the
              current version when creating new packaging, website content, or
              marketing materials.
            </p>
            <p className="description-text">
              Material changes will be communicated to registered organizations
              using the contact information supplied during registration.
            </p>
            <p className="description-text">
              Previously produced materials will be handled reasonably and in
              consultation with OVR when a guideline change affects an existing
              use.
            </p>
          </div>

          {/* Section 20 */}
          <div className="seal-guidelines-section">
            <h2 className="sub-headline">
              20. Questions and Usage Support
            </h2>
            <p className="description-text">
              For questions about eligibility, age-seal selection, artwork, or
              a proposed use, contact:
            </p>
            <p className="description-text">
              <a href="mailto:oldvineregistry@gmail.com">
                oldvineregistry@gmail.com
              </a>
            </p>
            <p className="description-text">
              When requesting guidance, include:
            </p>
            <ul className="description-text">
              <li>Your organization name</li>
              <li>The wine and vintage, if applicable</li>
              <li>Links to the relevant OVR vineyard records</li>
              <li>The age seal you intend to use</li>
              <li>
                A description or image of the proposed application
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};
